import type {ChatInputRef} from '@github-ui/copilot-chat/components/ChatInput'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {Layout as PacerLayout} from '@github-ui/pacer/Layout'
import {Spinner, Stack, IconButton} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {addUrlToHistoryStack} from '@github-ui/history'
import {SessionContextProvider} from '../../contexts/SessionContext'
import {useSessions} from '../../hooks/useSessions'
import {PLAN_STATE_APPROVED, PLAN_STATE_DISMISSED} from '../../hooks/use-patch-session'
import {usePendingPlanGate} from '../../hooks/use-pending-plan-gate'
import type {AgentTaskAppPayload} from '../../types/agent-task-app-payload'
import {extractSpecTaskThreadIdFromEventIdentifiers} from '../../utils/extract-spec-task-thread-id'
import {COPILOT_CHAT_SPEC_TASK_EVENT_TYPE} from '../../utils/constants'
import {
  isActiveSession,
  isCCRAgent,
  isReadonlyAgent,
  isPendingSession,
  type Session as SessionType,
  type ChatMessage,
} from '../../types/session'
import {
  CCA_PLAN_ACTION_IMPLEMENT,
  CCA_PLAN_ACTION_EXIT,
  CCA_PLAN_ACTIONS,
  CopilotFinalStopMessage,
} from './messages/CopilotFinalStopMessage'
import {UserInitialMessage} from './messages/UserInitialMessage'
import {Session} from './Session'
import {PlanApprovalDisplay} from '../session-logs/PlanApprovalDisplay'
import styles from './TaskChat.module.css'
import {useAgentMentions} from '../../hooks/useAgentMentions'
import {useTaskAuthor} from '../../hooks/use-task-author'
import {UserMessage} from './messages/UserMessage'
import {UserProblemStatementMessage} from './messages/UserProblemStatementMessage'
import type {AgentMention} from '../../types/agent-mention'
import {usePendingSession} from '../../hooks/usePendingSession'
import {useInactiveLogs} from '../../hooks/useInactiveLogs'
import {CopilotReplyMessage} from './messages/CopilotReplyMessage'
import {useCreateAPISession} from '../../hooks/use-create-api-session'
import {AlertIcon, ArrowDownIcon} from '@primer/octicons-react'
import {resolveAgentProfileForAPITask} from '../../agents/resolve-agent-profile'
import {useIsAhpSession} from '../../hooks/use-is-ahp-session'
import {Blankslate} from '@primer/react/experimental'
import {useAutoScroll} from '../../hooks/use-auto-scroll'
import {useDiffEventHandler} from '../../hooks/useDiffEventHandler'
import {CurrentUserProvider, useCurrentUser} from '../../contexts/CurrentUserContext'
import {TaskChatHeader} from './TaskChatHeader'
import {TaskChatInputV2} from './TaskChatInputV2'
import {DiffOverlay} from './DiffOverlay'
import {baseRefMatchesHeadRef, getBranchArtifactData, getGitHubArtifactData} from '../../utils/task-artifact-helpers'
import {clsx} from 'clsx'
import {useIntentDetection} from '../../hooks/useIntentDetection'
import {CopilotAnimationType} from '@github-ui/copilot-animation'
import {WithShimmerEffect} from '@github-ui/copilot-chat/components/WithShimmerEffect'
import {AgentAvatar} from '../AgentAvatar'
import {usePullRequest} from '../../hooks/usePullRequest'
import {useTask} from '../../hooks/use-task'
import type {HydratedAPITask} from '../../types/api-task'
import type {PublicUser} from '../../types/public-user'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ConsecutiveCAPI401sProvider} from '../../contexts/ConsecutiveCAPI401sContext'
import {
  useContentPreview,
  useContentPreviewOpen,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewContext'
import {CopilotNavigationWithContext} from '@github-ui/copilot-chat/components/navigation/CopilotNavigation'
import {CurrentRepositoryProvider, type Repository} from '@github-ui/current-repository'
import {TaskActionProviders} from '../task-list/TaskActionProviders'
import {ArchiveErrorBanner} from '../ArchiveErrorBanner'
import {SidebarAgentFilterProvider} from '../../contexts/SidebarAgentFilterContext'
import {useOrFetchOrCreateTaskPayload} from '../../hooks/useOrFetchOrCreateTask'
import {useIntentDetectionContext} from '../../contexts/IntentDetectionContext'
import {IntentDetectionEnabledProvider} from '../../contexts/IntentDetectionEnabledContext'

/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useParams} from '@github-ui/react-router'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ContentPreviewSlot} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewComponentContext'
import {useNavigableRef} from '@github-ui/navigable-ref'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {ChatStreamProvider, useChatStreamContext} from '../../contexts/ChatStreamContext'
import {LogChatMessage} from '../session-logs/SessionLogs'
import {useViewMode} from '../../contexts/ViewModeContext'
import {OptimisticLogsProvider} from '../../contexts/OptimisticLogsContext'
import {AgentProfileProvider} from '../../contexts/AgentProfileContext'
import {TaskViewNudge} from '../nudges/TaskViewNudge'
import {useLatestAgentModel} from '../../hooks/use-latest-agent-model'

// Encapsulates diff overlay state, refs, and callbacks used by TaskChat.
function useDiffOverlay({
  artifacts,
  pullBaseRef,
  pullHeadRef,
  hasActiveSession,
}: {
  artifacts: HydratedAPITask['artifacts']
  pullBaseRef: string | undefined
  pullHeadRef: string | undefined
  hasActiveSession: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [initialFilePath, setInitialFilePath] = useState<string | null>(null)

  const branchArtifact = getBranchArtifactData(artifacts)
  const baseRef = (!baseRefMatchesHeadRef(branchArtifact) && branchArtifact?.base_ref) || pullBaseRef
  const headRef = branchArtifact?.head_ref || pullHeadRef

  // Close the overlay when a session becomes active
  const [prevHasActiveSession, setPrevHasActiveSession] = useState(hasActiveSession)
  if (prevHasActiveSession !== hasActiveSession) {
    setPrevHasActiveSession(hasActiveSession)
    if (!prevHasActiveSession && hasActiveSession) {
      setIsOpen(false)
      setInitialFilePath(null)
    }
  }

  const close = useCallback(() => {
    setIsOpen(false)
    setInitialFilePath(null)
  }, [])

  const onViewDiff = useMemo(
    () => (filePath?: string) => {
      if (isOpen && !filePath) {
        setIsOpen(false)
        setInitialFilePath(null)
      } else {
        setInitialFilePath(filePath ?? null)
        setIsOpen(true)
      }
    },
    [isOpen],
  )

  const onCloseDiff = useMemo(() => close, [close])

  return {isOpen, initialFilePath, baseRef, headRef, close, onViewDiff, onCloseDiff}
}

interface TaskChatSuspendedWrapperPayload extends AgentTaskAppPayload {
  repository?: Repository
}

// Wrapper for showing the repo-agents style page, but under Copilot Immersive
export function TaskChatSuspendedWrapper() {
  return (
    <IntentDetectionEnabledProvider isUsingIntentDetection={isFeatureEnabled('copilot_intent_detection')}>
      <ChatStreamProvider>
        <TaskChatSuspendedWrapperInner />
      </ChatStreamProvider>
    </IntentDetectionEnabledProvider>
  )
}

function TaskChatSuspendedWrapperInner() {
  const {data: payload} = useOrFetchOrCreateTaskPayload()

  const {taskCreationRequest} = useIntentDetectionContext()
  const [createOwner, createRepo] = taskCreationRequest?.nwo?.split('/') || []

  const {taskID} = useParams<{taskID?: string}>()
  const navigate = useNavigate()

  const ref = useNavigableRef<HTMLTextAreaElement>()

  const isContentPreviewOpen = useContentPreviewOpen()
  const {openPreviewPane, closePreviewPane} = useContentPreview()

  const {currentUser, repository} = (payload || {}) as TaskChatSuspendedWrapperPayload

  // Once we're received the real payload after creation, reset the URL
  useEffect(() => {
    if (payload && taskID === 'new' && repository?.name) {
      navigate(`/copilot/t/${repository?.ownerLogin}/${repository?.name}/${payload.taskId}`, {replace: true})
    }
  }, [taskID, payload, repository?.ownerLogin, repository?.name, navigate])

  const originalPayload = useAppPayload<AgentTaskAppPayload>()

  // Check if the current user is a session owner by matching their ID against session user_ids.
  const currentUserIsSessionOwner = !!currentUser && !!payload?.sessions?.some(s => s.user_id === currentUser.id)

  const setContentPreviewPaneOpen = useCallback(
    (open: boolean) => {
      if (open) {
        openPreviewPane()
      } else {
        closePreviewPane()
      }
    },
    [openPreviewPane, closePreviewPane],
  )

  return (
    <SidebarAgentFilterProvider>
      <ConsecutiveCAPI401sProvider>
        <PacerLayout
          mainColumn="left"
          data-hpc
          secondaryColumnState={[isContentPreviewOpen, setContentPreviewPaneOpen]}
          slimLineTopTabs={copilotFeatureFlags.copilotWorkbenchSlimLineTopTabs}
        >
          <PacerLayout.Sidebar>
            <CopilotNavigationWithContext />
          </PacerLayout.Sidebar>
          <PacerLayout.ColumnLeft>
            {!payload && !taskCreationRequest && (
              <div className={styles.suspenseSpinner}>
                <Spinner size="medium" />
              </div>
            )}
            {!payload && taskCreationRequest && (
              <CurrentUserProvider currentUser={originalPayload?.currentUser} currentUserIsSessionOwner>
                <TaskChatDuringCreation
                  content={taskCreationRequest.problemStatement}
                  currentUser={originalPayload?.currentUser}
                  owner={createOwner}
                  repo={createRepo}
                />
              </CurrentUserProvider>
            )}
            {payload && repository && (
              <CurrentRepositoryProvider repository={repository}>
                <CurrentUserProvider
                  currentUser={payload.currentUser}
                  currentUserIsSessionOwner={currentUserIsSessionOwner}
                >
                  <TaskChat
                    key={payload.taskId}
                    repoOwner={repository.ownerLogin}
                    repoName={repository.name}
                    showHeader
                    navigateOnDelete
                    {...payload}
                  />
                </CurrentUserProvider>
              </CurrentRepositoryProvider>
            )}
            {payload && !repository && (
              <CurrentUserProvider
                currentUser={payload.currentUser}
                currentUserIsSessionOwner={currentUserIsSessionOwner}
              >
                <TaskChat key={payload.taskId} showHeader navigateOnDelete {...payload} />
              </CurrentUserProvider>
            )}
          </PacerLayout.ColumnLeft>
          <PacerLayout.ColumnRight>
            <ContentPreviewSlot returnFocusRef={ref} />
          </PacerLayout.ColumnRight>
        </PacerLayout>
      </ConsecutiveCAPI401sProvider>
    </SidebarAgentFilterProvider>
  )
}

// This component is the placeholder while task creation is actually happening
// on the backend. Its intent is to mimic what the real page will look like when
// we start getting the real data back.
function TaskChatDuringCreation({
  currentUser,
  content,
  owner,
  repo,
}: {
  currentUser: PublicUser | undefined
  content: string
  owner?: string
  repo?: string
}) {
  const [searchParams] = useSearchParams()
  const urlSessionId = searchParams.get('session_id')

  // This is hokey but provide just enough to get the components to play along
  const fakeSession = {
    event_content: content,
    event_type: 'chat',
    state: 'in_progress',
    user_id: currentUser?.id,
    user_login: currentUser?.login,
  } as SessionType

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.stickableContainer}>
          <div className={styles.content}>
            <div className={styles.taskChatDuringCreationContainer}>
              <TaskChatHeader
                sessions={[fakeSession]}
                currentSessionId={urlSessionId ?? undefined}
                pull={undefined}
                task={undefined}
                repoOwner={owner}
                repoName={repo}
              />
              <div className={styles.initialMessages}>
                <UserInitialMessage
                  currentUser={currentUser}
                  threadId={undefined}
                  session={fakeSession}
                  message={''}
                  agentId={null}
                />
              </div>
              <Stack direction="horizontal" padding="normal" align="center" justify="start" gap="condensed">
                <AgentAvatar animationType={CopilotAnimationType.Thinking} loopAnimation />
                <WithShimmerEffect className="color-fg-muted">Planning response...</WithShimmerEffect>
              </Stack>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function CenteredSpinner() {
  return (
    <div className={styles.blankSlateContainer} style={{alignItems: 'center'}}>
      <Spinner />
    </div>
  )
}

export function CenteredBlankSlate({
  title,
  description,
  statusLink,
  pullLink,
}: {
  title: string
  description: string
  statusLink?: string
  pullLink?: string
}) {
  return (
    <div className={styles.blankSlateContainer}>
      <Blankslate>
        <Blankslate.Visual>
          <AlertIcon size={24} className="fgColor-muted" />
        </Blankslate.Visual>
        <Blankslate.Heading>{title}</Blankslate.Heading>
        <Blankslate.Description className="text-center">{description}</Blankslate.Description>
        {statusLink ? <Blankslate.SecondaryAction href={statusLink}>GitHub status</Blankslate.SecondaryAction> : null}
        {pullLink ? <Blankslate.SecondaryAction href={pullLink}>Go to pull request</Blankslate.SecondaryAction> : null}
      </Blankslate>
    </div>
  )
}

interface TaskChatProps extends AgentTaskAppPayload {
  showHeader?: boolean
  repoOwner?: string
  repoName?: string
  taskId?: string
  branchAliveChannel?: string
  task?: HydratedAPITask
  hideArchive?: boolean
  cliRemoteNoticeDismissed?: boolean
  navigateOnDelete?: boolean
}

const ScrollToBottomButton = ({isAtBottom, onScrollToBottom}: {isAtBottom: boolean; onScrollToBottom: () => void}) => {
  if (isAtBottom) return null

  return (
    <IconButton
      aria-label="Scroll to bottom"
      className={styles.scrollToBottomButton}
      onClick={onScrollToBottom}
      icon={ArrowDownIcon}
      tooltipDirection="n"
    />
  )
}

export function TaskChat({
  showHeader = false,
  repoOwner,
  repoName,
  taskId,
  branchAliveChannel,
  task,
  hideArchive = false,
  cliRemoteNoticeDismissed,
  navigateOnDelete,
  ...payload
}: TaskChatProps) {
  const {
    agentMentions,
    pull,
    sessions,
    logsPollingInterval = 3000,
    sessionsPollingInterval = 3000,
    remoteControlPolicyEnabled,
    initialAuthToken,
  } = payload

  const effectiveTaskId = taskId ?? pull?.global_relay_id
  const repoNwo = pull?.repository_nwo || (repoOwner && repoName ? `${repoOwner}/${repoName}` : '')
  const viewMode = useViewMode()
  const isSidePanel = viewMode === 'sidePanel'

  const [searchParams] = useSearchParams()
  const urlSessionId = searchParams.get('session_id')
  const {currentUser, currentUserIsSessionOwner} = useCurrentUser()
  const [highlightedSessionId, setHighlightedSessionId] = useState<string | null>(urlSessionId)
  // Prevents multiple removals of session_id from the URL for the same urlSessionId.
  const sessionIdClearedRef = useRef(false)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const chatInputRef = useRef<ChatInputRef>(null)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const initialDataSpinnerEnabled = isFeatureEnabled('copilot_mission_control_initial_data_spinner')

  const [selectedModelId] = useLatestAgentModel()

  const {streamingState, pendingChatMessage, clearPendingChat, latestChatSessionId, getChatMessageHistory} =
    useChatStreamContext()
  const {isUsingIntentDetection} = useIntentDetection()

  const {data: taskData, dataUpdatedAt: taskDataUpdatedAt} = useTask({
    owner: repoOwner,
    repo: repoName,
    taskId,
    initialTask: task,
  })

  const {createSession: createNewSession} = useCreateAPISession()

  const handlePlanApprove = (session: SessionType) => (action: string, planSummary: string) => {
    if (!effectiveTaskId) return
    if (action === CCA_PLAN_ACTION_EXIT) {
      updatePlanState(session.id, PLAN_STATE_DISMISSED)
      // Use setTimeout to ensure the textarea is ready to receive focus after React re-renders
      setTimeout(() => {
        textAreaRef.current?.focus()
      }, 0)
      return
    }
    updatePlanState(session.id, PLAN_STATE_APPROVED)
    const prompt = action === CCA_PLAN_ACTION_IMPLEMENT ? `Implement the plan:\n\n${planSummary}` : action
    createNewSession({
      nwo: repoNwo,
      taskId: effectiveTaskId,
      problemStatement: prompt,
      model: selectedModelId,
      eventType: 'agent_task_post_session_steering',
    })
  }

  const agentProfile = resolveAgentProfileForAPITask(taskData)

  // When the agent creates a PR, the task artifacts will contain a github_resource artifact.
  // Use usePullRequest to reactively fetch the PR data without a full page reload.
  const taskPullRequestId = taskData ? getGitHubArtifactData(taskData.artifacts)?.id : undefined
  const {data: detectedPull} = usePullRequest({
    pullRequestId: !pull ? taskPullRequestId : undefined,
    includeBody: false,
    refetchOnMount: true,
  })

  // Use the detected PR if the original payload didn't include one
  const effectivePull = pull ?? detectedPull
  const effectivePayload = {...payload, pull: effectivePull}

  const {
    data: sessionsData,
    isError: sessionsIsError,
    timedOut: sessionsTimedOut,
    isFetchedAfterMount: notUsingSessionInitialData,
  } = useSessions({
    pullId: effectivePull?.id,
    taskId,
    initialData: sessions,
    sessionsPollingInterval,
    clearPendingChat,
  })

  const effectiveLogsPollingInterval = agentProfile.interactiveSession ? 1000 : logsPollingInterval

  const hasActiveSession = sessionsData?.some(isActiveSession) ?? false

  const diffOverlay = useDiffOverlay({
    artifacts: taskData?.artifacts,
    pullBaseRef: effectivePull?.base_ref_name,
    pullHeadRef: effectivePull?.head_ref_name,
    hasActiveSession,
  })

  const mostRecentSession = sessionsData?.[sessionsData.length - 1]

  // Fetch mentions for the current sessions
  const {data: mentions, isFetching: isFetchingMentions} = useAgentMentions({
    pull: effectivePull,
    sessions: sessionsData,
    initialData: agentMentions ?? [],
    agentId: mostRecentSession?.agent_id,
  })

  // Manage pending sessions and mentions
  const {allSessions, allMentions, addPendingSession, clearPendingSession} = usePendingSession({
    pullId: effectivePull?.id,
    taskId: effectiveTaskId,
    sessions: sessionsData,
    mentions,
    currentUser,
  })

  const clearPending = () => {
    clearPendingSession()
  }

  // For 'readonly' sessions like Copilot code review
  const everySessionReadonly = sessionsData?.every(s => isReadonlyAgent(s)) ?? false
  const everySessionCCR = sessionsData?.every(s => isCCRAgent(s)) ?? false

  // Sort sessions by creation time
  const sortedSessions = allSessions.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
  const lastSession = allSessions.at(-1)
  // Task-level eligibility: if the most recent session is AHP-backed, treat
  // the whole task that way for header/intent rendering. All sessions in a
  // task share an environment so a single check is sufficient.
  const {eligible: isAhpTask} = useIsAhpSession(lastSession ?? null)

  const {planStates, hasPendingPlan, pendingPlanContent, setPendingPlanContent, updatePlanState} = usePendingPlanGate({
    sessionsData,
    lastSessionId: lastSession?.id,
  })

  const {
    data: inactiveLogs,
    isLoading: inactiveLogsIsLoading,
    isFetching: inactiveLogsIsFetching,
  } = useInactiveLogs({
    sessions: sortedSessions,
    pullId: effectivePull?.id,
    repoOwner,
    repoName,
    taskId,
  })

  // Extract thread ID from the copilot-chat-spec-task session's event identifiers (feature flagged)
  const isTaskHyperlinkingEnabled = isFeatureEnabled('copilot_immersive_task_hyperlinking')
  const threadId = isTaskHyperlinkingEnabled
    ? (() => {
        const specTaskSession = allSessions.find(session => session.event_type === COPILOT_CHAT_SPEC_TASK_EVENT_TYPE)
        return extractSpecTaskThreadIdFromEventIdentifiers(specTaskSession?.event_identifiers)
      })()
    : undefined

  const taskAuthor = useTaskAuthor(taskData, repoOwner, repoName)

  // Function to get the mention for a specific session
  const getMentionForSession = (session: SessionType): AgentMention | undefined => {
    if (!allMentions || !session.event_identifiers) {
      return undefined
    }

    // There should only be one mention
    return allMentions.find(mention => session.event_identifiers?.includes(mention.id.toString()))
  }

  // Determine which session should be expanded by default
  const getDefaultOpenState = (sessionId: string, isPending: boolean) => {
    // CLI sessions should always be expanded
    if (agentProfile.interactiveSession) {
      return true
    }

    if (urlSessionId) {
      // If a specific session ID is provided in URL, expand that session
      return sessionId === urlSessionId
    }

    // We're expanding pending sessions by default
    if (isPending) {
      return true
    }

    // If there's an active session, that one takes precedence unless explicitly provided above
    if (sessionsData && sessionsData.some(session => isActiveSession(session))) {
      return sessionId === sessionsData.find(session => isActiveSession(session))?.id
    }
    return false
  }

  const {isAtBottom, scrollToBottom} = useAutoScroll({
    containerRef: scrollContainerRef,
    sentinelRef,
    contentRef,
    resetKey: effectiveTaskId,
    offset: 4,
  })

  // Adapter for TaskChatInputV2 which passes an offset option (no longer needed since
  // scrollIntoView on the sentinel handles positioning correctly)
  const scrollToBottomCompat = useCallback(
    (_options?: {offset?: number}) => {
      scrollToBottom()
    },
    [scrollToBottom],
  )

  const handleCommandSent = useCallback(() => {
    // defer to next frame so that the new message is rendered before we attempt to scroll
    requestAnimationFrame(() => {
      scrollToBottom()
    })
  }, [scrollToBottom])

  const removeHighlight = useCallback(() => {
    if (!urlSessionId) return
    if (typeof window === 'undefined') return
    if (sessionIdClearedRef.current) return

    sessionIdClearedRef.current = true
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('session_id')
    const searchString = newParams.toString()
    const newUrl = `${window.location.pathname}${searchString ? `?${searchString}` : ''}${window.location.hash}`
    addUrlToHistoryStack(newUrl)
    queueMicrotask(() => setHighlightedSessionId(null))
  }, [searchParams, urlSessionId])

  // Remove session_id from URL when a new session becomes active.
  useEffect(() => {
    if (!urlSessionId || !sessionsData) return

    const activeSession = sessionsData.find(isActiveSession)
    const activeSessionId = activeSession?.id ?? null

    if (!activeSessionId || activeSessionId === urlSessionId) return

    removeHighlight()
  }, [urlSessionId, sessionsData, removeHighlight])

  // If session_id changes in the URL, reset sessionIdClearedRef to allow possible removal of it from the URL.
  useEffect(() => {
    sessionIdClearedRef.current = false
  }, [urlSessionId])

  // Handle diff events to populate chat input with file references
  useDiffEventHandler({
    chatInputRef,
    textAreaRef,
    isReadonly: everySessionReadonly,
    taskId: effectiveTaskId ?? '',
  })

  // Clear chat input when switching tasks/pulls
  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.setText('')
    }
  }, [effectiveTaskId, urlSessionId])

  // Determine whether the pending chat section should be shown, and in what mode.
  //
  // showPendingUserMessage: We have a pending message and are still waiting for the server
  // to respond (streamingState === 'requested'). This naturally hides the pending message
  // after a 201 mid-session steering response (which goes straight to 'closed' without
  // streaming), since the message is already delivered as a command to the active session.
  //
  // showStreamingChatBridge: The streaming response has started (or finished) for a new
  // chat session but that session hasn't appeared in the sessions list yet (useSessions
  // hasn't polled it). We bridge the gap by rendering part of getChatMessageHistory() and
  // the planning spinner so we don't get a blank area waiting on the session to load.
  const latestChatSessionLoaded = latestChatSessionId ? sortedSessions.some(s => s.id === latestChatSessionId) : true
  const showPendingUserMessage = isUsingIntentDetection && !!pendingChatMessage && streamingState === 'requested'
  const showStreamingChatBridge = isUsingIntentDetection && !!latestChatSessionId && !latestChatSessionLoaded

  // When bridging the gap before a new chat session loads, recover the flushed user
  // message from getChatMessageHistory (pendingChatMessage was already cleared by
  // setStreamingChatResponse). We only grab the first entry and skip the streaming
  // assistant response to avoid a glitchy content swap when the full session loads.
  const bridgeUserMessage =
    showStreamingChatBridge && latestChatSessionId
      ? getChatMessageHistory(latestChatSessionId).find(msg => msg.role === 'user')
      : null

  return !sessionsData ? (
    <CenteredSpinner />
  ) : sessionsIsError && sessionsTimedOut ? (
    <CenteredBlankSlate
      title="Activity cannot be loaded"
      description="The activity on this task is temporarily unavailable due to a system error. Try reloading the page, or if the problem persists, contact support."
      statusLink="https://www.githubstatus.com/"
    />
  ) : !sessionsData.length && initialDataSpinnerEnabled && !notUsingSessionInitialData ? (
    <CenteredSpinner />
  ) : !sessionsData.length ? (
    <CenteredBlankSlate
      title="This task is not viewable"
      description={`You can't view this task because you do not have any active sessions${
        effectivePull ? ' for this pull request' : ''
      }.`}
      pullLink={effectivePull?.url}
    />
  ) : (
    <TaskActionProviders navigateOnDelete={navigateOnDelete}>
      <AgentProfileProvider slug={taskData?.agent_collaborators?.[0]?.slug}>
        <OptimisticLogsProvider
          sessionId={agentProfile.interactiveSession ? lastSession?.id : undefined}
          taskId={effectiveTaskId}
        >
          <div className={styles.root}>
            <div className={styles.container}>
              <div className={styles.diffOverlayWrapper}>
                <div className={clsx(styles.stickableContainer, diffOverlay.isOpen && styles.stickableContainerFrozen)}>
                  <div ref={scrollContainerRef} className={styles.scrollContainer}>
                    <ScrollToBottomButton isAtBottom={isAtBottom} onScrollToBottom={() => scrollToBottom('smooth')} />
                    <div ref={contentRef} className={clsx(styles.content, isSidePanel && styles.sidePanelContent)}>
                      <ArchiveErrorBanner />
                      {showHeader && (
                        <TaskChatHeader
                          sessions={sortedSessions}
                          currentSessionId={urlSessionId ?? undefined}
                          pull={effectivePull}
                          task={taskData}
                          repoOwner={repoOwner}
                          repoName={repoName}
                          hideArchive={hideArchive}
                          isCodeReviewMode={everySessionCCR}
                          dataUpdatedAt={taskDataUpdatedAt}
                        />
                      )}
                      <TaskViewNudge taskCompleted={taskData?.status === 'completed'} />
                      {sortedSessions.map((session, index) => {
                        const isFirstSession = index === 0
                        const mention = getMentionForSession(session)
                        const isPending = isPendingSession(session)
                        const isReadonly = isReadonlyAgent(session)
                        const isCCR = isCCRAgent(session)

                        return (
                          <div className="d-flex flex-column" key={session.id}>
                            {/* Skip initial messages for interactive sessions and AHP-backed
                                sessions — neither has meaningful "started a task" context. */}
                            {isFirstSession && !agentProfile.agentProvidesIntent && !isAhpTask && (
                              <div className={styles.initialMessages}>
                                <UserInitialMessage
                                  currentUser={currentUser}
                                  threadId={threadId}
                                  session={session}
                                  message={isCCR ? 'requested a review' : 'started a task'}
                                  agentId={session.agent_id}
                                  isTaskCreatedByAutomation={taskAuthor.isAutomation}
                                  automationDisplayName={taskAuthor.isAutomation ? taskAuthor.displayName : undefined}
                                />
                              </div>
                            )}
                            {/* Subsequent sessions: show PR comment mention or problem statement */}
                            {!isFirstSession && !isReadonly && (mention || isFetchingMentions) && (
                              <UserMessage
                                key={mention?.id}
                                mention={mention}
                                isLoading={!mention && isFetchingMentions}
                                agentId={session.agent_id}
                              />
                            )}
                            {!isFirstSession && !isReadonly && !mention && !isFetchingMentions && (
                              <UserProblemStatementMessage session={session} />
                            )}
                            <SessionContextProvider
                              session={session}
                              logs={inactiveLogs?.[session.id]}
                              logsAreLoading={
                                inactiveLogsIsLoading ||
                                (inactiveLogsIsFetching && inactiveLogs?.[session.id] === undefined)
                              }
                              taskSharingStatus={taskData?.sharing_status}
                            >
                              <Session
                                defaultOpen={getDefaultOpenState(session.id, isPending)}
                                onOpenChange={open => {
                                  if (!open && session.id === highlightedSessionId) {
                                    removeHighlight()
                                  }
                                }}
                                shouldHighlight={session.id === highlightedSessionId}
                                pull={effectivePull}
                                logsPollingInterval={effectiveLogsPollingInterval}
                                nwo={repoNwo}
                              />
                              {session.state === 'completed' && (
                                <>
                                  {/* Copilot never has a reply on the first session, but in single session view we can't tell, so show it to be safe */}
                                  {((currentUserIsSessionOwner && !isFirstSession) || !currentUserIsSessionOwner) && (
                                    <CopilotReplyMessage logsPollingInterval={effectiveLogsPollingInterval ?? 3000} />
                                  )}
                                  {/* For pull_request_comment sessions we render reply_to_comment tool outside of logs instead. */}
                                  {session.event_type !== 'pull_request_comment' && (
                                    <CopilotFinalStopMessage
                                      logsPollingInterval={effectiveLogsPollingInterval ?? 3000}
                                      onApprove={
                                        planStates[session.id] === PLAN_STATE_APPROVED
                                          ? undefined
                                          : handlePlanApprove(session)
                                      }
                                      planState={planStates[session.id]}
                                      onPlanDetected={
                                        index === sortedSessions.length - 1 && !planStates[session.id]
                                          ? setPendingPlanContent
                                          : undefined
                                      }
                                    />
                                  )}
                                </>
                              )}
                            </SessionContextProvider>
                          </div>
                        )
                      })}
                      {(showPendingUserMessage || showStreamingChatBridge) && (
                        <>
                          {showPendingUserMessage &&
                            sortedSessions[sortedSessions.length - 1]?.event_type !== 'chat' && (
                              <LogChatMessage logEntry={pendingChatMessage as ChatMessage} />
                            )}
                          {showStreamingChatBridge && bridgeUserMessage && (
                            <LogChatMessage logEntry={bridgeUserMessage as ChatMessage} />
                          )}
                          <Stack direction="horizontal" padding="normal" align="center" justify="start" gap="condensed">
                            <AgentAvatar animationType={CopilotAnimationType.Thinking} loopAnimation />
                            <WithShimmerEffect className="color-fg-muted">Planning response...</WithShimmerEffect>
                          </Stack>
                        </>
                      )}
                      {/* used for auto-scrolling */}
                      <div ref={sentinelRef} aria-hidden style={{height: 1}} />
                    </div>
                  </div>
                </div>
                {!effectivePull && diffOverlay.baseRef && diffOverlay.headRef && repoNwo && effectiveTaskId && (
                  <DiffOverlay
                    isOpen={diffOverlay.isOpen}
                    onClose={diffOverlay.close}
                    repoNwo={repoNwo}
                    taskId={effectiveTaskId}
                    baseRef={diffOverlay.baseRef}
                    headRef={diffOverlay.headRef}
                    initialFilePath={diffOverlay.initialFilePath}
                  />
                )}
              </div>
              {!everySessionReadonly &&
                (hasPendingPlan && pendingPlanContent && lastSession ? (
                  <div className={clsx(styles.chatInputContainer, !isAtBottom && styles.chatInputContainerFaded)}>
                    <PlanApprovalDisplay
                      summary={pendingPlanContent}
                      planContent={pendingPlanContent}
                      actions={CCA_PLAN_ACTIONS}
                      recommendedAction="implement"
                      onAction={action => handlePlanApprove(lastSession)(action, pendingPlanContent)}
                      isTokenBasedBillingEnabled={effectivePayload.useTokenBasedBilling}
                    />
                  </div>
                ) : (
                  <div className={clsx(styles.chatInputContainer, !isAtBottom && styles.chatInputContainerFaded)}>
                    {agentProfile.slots.ChatInput && lastSession ? (
                      <agentProfile.slots.ChatInput
                        sessionId={lastSession.id}
                        session={lastSession}
                        task={taskData}
                        onCommandSent={handleCommandSent}
                        cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
                        remoteControlPolicyEnabled={remoteControlPolicyEnabled}
                        ssoOrgIds={initialAuthToken?.ssoOrgIDs}
                      />
                    ) : taskData ? (
                      <TaskChatInputV2
                        taskAgentId={allSessions[0]?.agent_id}
                        activeSession={
                          // Prefer an active session that's actively doing work over
                          // a paused (waiting_for_user) one. During a resume, the OLD
                          // session is permanently waiting_for_user (frozen) while the
                          // NEW resume session is in_progress; we want UI affordances
                          // (Stop button, chat input header, etc.) to track the live
                          // resume session, not the frozen original.
                          allSessions?.find(s => isActiveSession(s) && s.state !== 'waiting_for_user') ??
                          allSessions?.find(isActiveSession) ??
                          null
                        }
                        onAddPendingSession={addPendingSession}
                        onClearPendingSession={clearPending}
                        payload={effectivePayload}
                        scrollToBottom={scrollToBottomCompat}
                        repoNwo={repoNwo}
                        task={taskData}
                        branchAliveChannel={branchAliveChannel}
                        onCloseDiff={diffOverlay.onCloseDiff}
                        onViewDiff={diffOverlay.onViewDiff}
                      />
                    ) : null}
                  </div>
                ))}
            </div>
          </div>
        </OptimisticLogsProvider>
      </AgentProfileProvider>
    </TaskActionProviders>
  )
}
